import { Alert, AlertTitle, List, ListItem, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ValidationError } from '@/lib/validationErrors';
import { HelpReadMoreLine } from '@/features/help/HelpReadMore';

interface DialogValidationErrorsProps {
  errors: ValidationError[];
  onClear?: () => void;
}

/**
 * Modal-adapted error display: the standard ValidationErrorBanner + ValidationErrorPanel pair is
 * built for full-page views (the panel is a right-side drawer) and breaks inside a Dialog. This
 * renders the same ValidationError list as a single dismissible Alert — one message inline, several
 * as a compact list — so command dialogs get readable errors without the drawer machinery. A refusal whose
 * error code or failed rule names a help section offers "Read more", which opens the help panel over the dialog.
 */
export function DialogValidationErrors({ errors, onClear }: DialogValidationErrorsProps) {
  const { t } = useTranslation();
  if (errors.length === 0) {
    return null;
  }
  // One response's refusals share their section (getEnvelopeValidationErrors), so one link serves them all.
  const helpRef = errors.find((error) => error.helpRef)?.helpRef;

  return (
    <Alert severity="error" onClose={onClear} sx={{ mb: 2 }}>
      {errors.length === 1 ? (
        errors[0].message
      ) : (
        <>
          <AlertTitle>
            {t('validation.errorCount', {
              defaultValue: '{{count}} errors', count: errors.length,
            })}
          </AlertTitle>
          <List dense disablePadding>
            {errors.map((error, index) => (
              <ListItem key={`${error.field}-${index}`} disableGutters sx={{ py: 0 }}>
                <ListItemText
                  primary={error.field && error.field !== 'form' ? `${error.field}: ${error.message}` : error.message}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
      <HelpReadMoreLine sectionId={helpRef} />
    </Alert>
  );
}
